// src/utils/emailUtils.js

import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { COLLECTIONS } from '../platform/constants';

async function fetchActiveMemberDocs(db, tenantId) {
  const membersSnap = await getDocs(
    collection(db, COLLECTIONS.TENANTS, tenantId, COLLECTIONS.MEMBERS),
  );
  return membersSnap.docs.filter((memberDoc) => memberDoc.data()?.status !== 'inactive');
}

async function fetchUserProfilesByUid(db, uids) {
  if (uids.length === 0) {
    return new Map();
  }

  const userSnaps = await Promise.all(
    uids.map((uid) => getDoc(doc(db, COLLECTIONS.USERS, uid))),
  );

  const profiles = new Map();
  userSnaps.forEach((userSnap) => {
    if (userSnap.exists()) {
      profiles.set(userSnap.id, userSnap.data());
    }
  });
  return profiles;
}

/**
 * Fetch active member emails for the active tenant.
 */
export async function getAllUserEmailsForTenant(db, tenantId) {
  if (!tenantId) {
    return [];
  }

  const memberDocs = await fetchActiveMemberDocs(db, tenantId);
  const profiles = await fetchUserProfilesByUid(
    db,
    memberDocs.map((memberDoc) => memberDoc.id),
  );

  return memberDocs
    .map((memberDoc) => profiles.get(memberDoc.id)?.email || null)
    .filter(Boolean);
}

/**
 * Fetch member emails for a unit within the active tenant.
 */
export async function getUserEmailsByUnitForTenant(db, tenantId, unitId) {
  if (!tenantId || !unitId) {
    return [];
  }

  const memberDocs = await fetchActiveMemberDocs(db, tenantId);
  const profiles = await fetchUserProfilesByUid(
    db,
    memberDocs.map((memberDoc) => memberDoc.id),
  );
  const emails = [];

  for (const memberDoc of memberDocs) {
    const member = memberDoc.data() || {};
    const profile = profiles.get(memberDoc.id);
    const unitIds = Array.isArray(member.unitIds) ? member.unitIds : [];
    const profileUnit = profile?.unit || null;
    const matchesUnit = unitIds.includes(unitId) || profileUnit === unitId;

    if (!matchesUnit) {
      continue;
    }

    const email = profile?.email || null;
    if (email) {
      emails.push(email);
    }
  }

  return emails;
}

/**
 * Send a mass email by calling the Node/Express backend.
 */
export async function sendMassEmail(subject, html, recipients) {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/send-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_SECRET_KEY,
        },
        body: JSON.stringify({ subject, html, recipients }),
      },
    );

    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.error || 'Pošiljanje e-pošte ni uspelo.');
    }
    return await response.json();
  } catch (error) {
    console.error('sendMassEmail error:', error);
    throw error;
  }
}
